<script setup lang="ts">
import { PageEnum, SmokerStatusEnum } from "@/enums";
import { computed, ref, watch } from "vue";
import JourneyLayout from "@/components/layout/JourneyLayout/JourneyLayout.vue";
import AsideView from "@/views/AsideView.vue";
import PageHeading from "@/components/general/PageHeading/PageHeading.vue";
import Button from "@/components/general/Button/Button.vue";
import Dropdown from "@/components/general/Dropdown/Dropdown.vue";
import {
    useCustomerStore,
    useDataLayerStore,
    useGlobalStore,
    useQuoteStore,
    useJourneyStore,
} from "@/stores";
import QuestionWithHelp from "@/components/general/Question/Question.vue";
import DateField from "@/components/general/DateField/DateField.vue";
import RadioGroup from "@/components/general/RadioGroup/RadioGroup.vue";
import TextField from "@/components/general/TextField/TextField.vue";
import useVuelidate from "@vuelidate/core";
import type { ErrorObject } from "@vuelidate/core";
import { getQuoteRules } from "@/validation";

import { AdobeEventTypeEnum, EventTypeEnum } from "@/models";
import Checkbox from "@/components/general/Checkbox/Checkbox.vue";

// Stores
const customer = useCustomerStore();
const quote = useQuoteStore();
const dataLayer = useDataLayerStore();
const global = useGlobalStore();
const journey = useJourneyStore();

const emit = defineEmits<{
    submitSection: [currentPage: PageEnum, nextPage: PageEnum];
}>();

const v$ = useVuelidate(getQuoteRules(customer), { customer, journey });

const detailChanged = (state: string, key: string) => {
    v$.value[state][key].$touch();
    if (v$.value[state][key].$error) {
        dataLayer.errors = v$.value[state].$errors;
        dataLayer.triggerEvent(EventTypeEnum.ErrorLoad, key);
    }
    dataLayer.triggerEvent(EventTypeEnum.QuestComplete, key);
};

const monthlyTouched = ref(false);
const monthlyError = computed(() => {
    if (!monthlyTouched.value) return "";
    if (quote.availableDeals.length === 0) {
        return `We need to know how much you would like to pay per month.
                To see your options, ensure that you have filled in your date of birth and smoker status correctly.`;
    }
    if (quote.myDeal < 0) {
        return "We need to know how much you would like to pay per month.";
    }
    return "";
});

const submitSection = async (currentPage: PageEnum, nextPage: PageEnum) => {
    monthlyTouched.value = true;
    dataLayer.triggerEvent(EventTypeEnum.PageInteraction, "button|Get a Quote");
    dataLayer.errors = [];
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect || monthlyError.value) {
        dataLayer.errors = v$.value.$errors;
        dataLayer.triggerEvent(EventTypeEnum.ErrorLoad);

        for (const error of v$.value.$errors) {
            dataLayer.triggerAdobeEvent(
                AdobeEventTypeEnum.FormFieldError,
                error.$propertyPath
            );
        }
        document
            .querySelector(".form-group .error")
            ?.closest(".form-group")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
    }
    quote.myQuote = quote.myDeal;
    emit("submitSection", currentPage, nextPage);
};

const monthlyPremiumOptions = computed(() => {
    return quote.availableDeals.map((rate, index) => ({
        label: `£${rate.monthlyCost}`,
        value: index,
    }));
});

const monthlyPremiumHelpText = computed(() => {
    return `The amount you can pay per month is dependent on your age and your smoker status. The minimum you can pay is £${quote.minPPM} per month and the maximum you can pay is £${quote.maxPPM} per month.`;
});

const ncEmailChange = (): void => {
    customer.gdpr.email = customer.ncEmail;
    dataLayer.triggerEvent(
        EventTypeEnum.QuestComplete,
        customer.ncEmail === true ? "NCEmailUnTicked" : "NCEmailTicked"
    );
};

const dobHasError = computed(() => {
    const dateGroupError =
        v$.value.customer.age.$error || v$.value.customer.fullDob.$error;

    return {
        day: v$.value.customer.dobDate.$error || dateGroupError,
        month: v$.value.customer.dobMonth.$error || dateGroupError,
        year: v$.value.customer.dobYear.$error || dateGroupError,
    };
});

const validateDate = (): void => {
    if (
        customer.dobYear !== "" &&
        customer.dobMonth !== "" &&
        customer.dobDate !== ""
    ) {
        v$.value.customer.age.$validate();
        v$.value.customer.fullDob.$validate();

        if (v$.value.customer.fullDob.$error) {
            dataLayer.errors = v$.value.customer.$errors;
            dataLayer.triggerEvent(EventTypeEnum.ErrorLoad, "fullDob");
        }

        if (v$.value.customer.age.$error) {
            dataLayer.errors = v$.value.customer.$errors;
            dataLayer.triggerEvent(EventTypeEnum.ErrorLoad, "age");
        }
    }
};

const handleDateBlur = (field: "date" | "month" | "year"): void => {
    dataLayer.triggerEvent(EventTypeEnum.PageInteraction, `blur|DOB|${field}`);
};

const dateValidationChecker = (field: {
    $errors: ErrorObject[];
    $dirty: boolean;
}): string => {
    const fullDateNotAvailable =
        customer.dobYear === "" ||
        customer.dobMonth === "" ||
        customer.dobDate === "";
    const invalidDateOfBirth =
        field.$errors.length ||
        v$.value.customer.age.$errors.length ||
        v$.value.customer.fullDob.$errors.length;

    if (fullDateNotAvailable) {
        return "";
    }

    return invalidDateOfBirth ? "error" : "valid";
};

const getErrorMessage = (errors: ErrorObject[] | undefined): string => {
    const message = errors?.[0]?.$message;
    return typeof message === "string" ? message : "";
};

const updateDeals = (questComplete?: string) => {
    if (quote.myDeal >= 0) {
        monthlyTouched.value = true;
    }
    quote.getDeals(questComplete);
};

const monthlyPremiumChange = () => {
    if (global.step > 1) {
        global.step = 1;
    }
    dataLayer.triggerAdobeEvent(
        AdobeEventTypeEnum.FormFieldInteraction,
        "monthlyPremium"
    );
    dataLayer.triggerEvent(
        EventTypeEnum.PageInteraction,
        `blur|monthlyPremium`
    );
};

watch(monthlyError, (newV: string, oldV: string) => {
    const errorMessageUpdated =
        oldV.includes("We need to know how much") &&
        newV.includes("We need to know how much");
    if (newV !== "" && !errorMessageUpdated) {
        dataLayer.triggerAdobeEvent(
            AdobeEventTypeEnum.FormFieldError,
            "monthlyPremium"
        );
    }
});

defineExpose({
    submitSection,
    ncEmailChange,
    v$,
    monthlyTouched,
    monthlyError,
    validateDate,
    dateValidationChecker,
    updateDeals,
    monthlyPremiumChange,
});
</script>

<template>
    <JourneyLayout>
        <template #main>
            <div :id="PageEnum.GetQuote">
                <div v-if="global.showDelayModal" class="delay-model">
                    <div class="delay-model-content">
                        <p>Loading your quote...</p>
                        <div class="spinner-ring spinner-rainbow" />
                    </div>
                </div>
                <p>
                    The plan you are applying for is designed to meet the needs
                    of people who are looking for a guaranteed acceptance life
                    insurance plan to pay a cash sum upon death. If this plan
                    meets your needs please continue with the application. The
                    following questions must only be answered by the person to
                    be insured and they must be resident in England, Wales,
                    Scotland or Northern Ireland.
                </p>
                <div v-if="journey.isAgentJourney" class="agent-journey">
                    <PageHeading> TSB Partner Details </PageHeading>
                    <QuestionWithHelp
                        id="staffId"
                        label="Type your TSB Partner ID here e.g. I123456"
                        :error-message="
                            getErrorMessage(v$.journey.staffId?.$errors)
                        "
                    >
                        <TextField
                            id="staffId-text"
                            v-model="journey.staffId"
                            type="text"
                            placeholder="Staff ID"
                            :has-error="v$.journey.staffId.$error"
                            @blur="detailChanged('journey', 'staffId')"
                        />
                    </QuestionWithHelp>
                    <Checkbox
                        id="advisor-checkbox"
                        v-model="journey.isAdvisor"
                        label="If you are a TSB Mortgage Advisor, please tick this box"
                        bold
                    />
                </div>
                <form
                    v-cloak
                    name="form1"
                    method="post"
                    autocomplete="off"
                    data-vv-scope="form1"
                >
                    <PageHeading class="slider-header">
                        Get a Quote
                    </PageHeading>
                    <QuestionWithHelp
                        id="DOB"
                        label="Your date of birth"
                        help-text="You must be between the ages of 50 and 80, a UK resident and have spent at least 183 days in the UK in the last tax year."
                        :error-message="
                            getErrorMessage(v$.customer.age?.$errors) ||
                            getErrorMessage(v$.customer.fullDob?.$errors)
                        "
                        @help-clicked="
                            () =>
                                dataLayer.triggerEvent(
                                    EventTypeEnum.PageInteraction,
                                    'question|DOB'
                                )
                        "
                    >
                        <DateField
                            :has-error="dobHasError"
                            :on-validate="validateDate"
                            :on-get-deals="updateDeals"
                            @blur="handleDateBlur"
                        />
                    </QuestionWithHelp>
                    <QuestionWithHelp
                        id="smokerStatus"
                        label="During the last 12 months have you smoked any cigarettes, cigars, a pipe (including shisha/hookah), used vapes, e-cigarettes, or nicotine replacements?"
                        help-text="If you've smoked any cigarettes, cigars, a pipe (including shisha/hookah), used vapes, e-cigarettes, or nicotine replacements at all in the last 12 months you need to answer 'yes - regularly' or 'yes - occasionally', even if the product used did not contain any nicotine."
                        :error-message="
                            getErrorMessage(v$.customer.smoker?.$errors)
                        "
                        @help-clicked="
                            () =>
                                dataLayer.triggerEvent(
                                    EventTypeEnum.PageInteraction,
                                    'question|smoker'
                                )
                        "
                    >
                        <RadioGroup
                            id="smokerStatus-radios"
                            v-model="customer.smoker"
                            input-name="smoker"
                            :options="[
                                {
                                    label: 'Yes, Regularly',
                                    value: SmokerStatusEnum.YesRegularly,
                                },
                                {
                                    label: 'Yes, Occasionally',
                                    value: SmokerStatusEnum.YesOccasionally,
                                },
                                {
                                    label: 'None at all',
                                    value: SmokerStatusEnum.NoneAtAll,
                                },
                            ]"
                            :has-error="v$.customer.smoker.$error"
                            @change="updateDeals('smokerStatus')"
                        />
                    </QuestionWithHelp>
                    <QuestionWithHelp
                        id="email"
                        label="Your email address"
                        :error-message="
                            getErrorMessage(v$.customer.email?.$errors)
                        "
                        :field-note="'We will only use your email address to follow up on details of your quote.'"
                        @help-clicked="
                            () =>
                                dataLayer.triggerEvent(
                                    EventTypeEnum.PageInteraction,
                                    'question|Email'
                                )
                        "
                    >
                        <template #help>
                            <ul class="help-list">
                                <li>
                                    Don’t worry, we will not use it to send you
                                    marketing material.
                                </li>
                                <li>
                                    We will send you a copy of your quote.
                                </li>
                                <li>
                                    We need this should you decide that you want
                                    to apply.
                                </li>
                            </ul>
                        </template>
                        <TextField
                            id="email-field"
                            v-model="customer.email"
                            type="email"
                            placeholder="Email address"
                            :has-error="v$.customer.email.$error"
                            @blur="detailChanged('customer', 'email')"
                        />
                    </QuestionWithHelp>

                    <QuestionWithHelp
                        id="monthlyPremium"
                        label="Please select a monthly premium for your quote"
                        :error-message="monthlyError"
                        :help-text="monthlyPremiumHelpText"
                        @help-clicked="
                            () =>
                                dataLayer.triggerEvent(
                                    EventTypeEnum.PageInteraction,
                                    'question|monthlyPremium'
                                )
                        "
                    >
                        <Dropdown
                            id="monthlyPremium-dropdown"
                            v-model="quote.myDeal"
                            :options="monthlyPremiumOptions"
                            :has-error="!!monthlyError"
                            placeholder="Select monthly premium"
                            :placeholder-value="-1"
                            aria-label="Monthly premium"
                            @change="monthlyPremiumChange"
                        />
                    </QuestionWithHelp>

                    <Button
                        :id="`submitPolicy`"
                        btn-type="primary"
                        class="continue"
                        @click.prevent="
                            submitSection(
                                PageEnum.GetQuote,
                                PageEnum.SelectCover
                            )
                        "
                    >
                        Get A Quote
                    </Button>
                </form>
            </div>
        </template>
        <template #aside>
            <AsideView
                :show-defaqto="false"
                :show-incentive="false"
                class="quote-aside"
            />
        </template>
    </JourneyLayout>
</template>

<style scoped>
.help-list {
    margin: 0;
    padding: 0 1.5rem;
}

.agent-journey {
    margin-bottom: 2rem;
}

@media screen and (width >= 993px) {
    .quote-aside {
        margin-top: 100px;
    }
}
</style>
